import React, { useCallback, useEffect, useLayoutEffect } from "react";
import { rootStore, RootStoreContext } from "./stores";
import { App } from "./containers/App";
import { ThemeProvider } from "styled-components";
import { initI18n } from "./locales/i18n";
import type { SolutionColors } from "@smartsuite/react-ui";
import type { UserPreferences } from "./types/user-preferences";
import { DEFAULT_USER_PREFERENCES } from "./defaults/user-preferences";
import { buildDefaultSolution } from "./defaults/solution";
import type { Solution } from "./types/solution";
import type { Member } from "./types/member";
import { DEFAULT_MEMBER } from "./defaults/member";
import { DEFAULT_ACCOUNT } from "./defaults/account";
import type { Account } from "./types/account.model";
import type { ApplicationModel } from "./types/application.model";
import { DEFAULT_RECORDS_LIMIT } from "./defaults/records-limit";
import { ImportType, DEFAULT_IMPORT_TYPE } from "./stores/config.store";
import type { ID } from "./types/core.model";
import { initTracing } from "@smartsuite/tracing";

export interface ImportUIProps {
  accountId: string;
  role: string;
  solution?: Solution;
  // for demo only , undefined on monolith
  accessToken?: string;
  lang: string;
  logoColor: SolutionColors;
  // for demo only , undefined on monolith
  smartsuiteBackendUrl?: string;
  // applications can be set by monolith only
  applications?: ApplicationModel[];
  // selected application id
  applicationId?: string;
  userPreferences?: UserPreferences;
  member?: Member;
  account?: Account;
  recordsLimit?: number;
  featureFlags?: Record<string, boolean>;
  importType?: ImportType;
  onClose?: () => void;
  onOpenCreateAppModal?: () => void;
  onSelectApplication?: (applicationId: ID) => void;
}

export const ImportUI: React.FC<ImportUIProps> = (props) => {
  const {
    accountId,
    role,
    applicationId,
    accessToken,
    smartsuiteBackendUrl,
    applications,
    userPreferences,
    solution,
    member,
    account,
    importType,
    recordsLimit,
    featureFlags,
  } = props;

  useLayoutEffect(
    () =>
      rootStore.init(
        accountId,
        role,
        applicationId,
        accessToken,
        smartsuiteBackendUrl ?? process.env.REACT_APP_SS_BACKEND_URL ?? "",
        applications,
        userPreferences ?? DEFAULT_USER_PREFERENCES,
        solution ?? buildDefaultSolution(),
        member ?? DEFAULT_MEMBER,
        account ?? DEFAULT_ACCOUNT,
        recordsLimit ?? DEFAULT_RECORDS_LIMIT,
        importType ?? DEFAULT_IMPORT_TYPE,
        featureFlags ?? {},
      ),
    [
      accountId,
      role,
      applicationId,
      accessToken,
      smartsuiteBackendUrl,
      applications,
      userPreferences,
      solution,
      member,
      account,
      importType,
      featureFlags,
    ],
  );

  const handleChangeMode = useCallback((e: KeyboardEvent) => {
    if (e.altKey && e.shiftKey && e.code === "KeyT") {
      rootStore.config.setImportType(
        rootStore.config.importType === ImportType.application
          ? ImportType.solution
          : ImportType.application,
      );
    }
  }, []);

  useEffect(() => {
    // attach the event listener
    document.addEventListener("keydown", handleChangeMode);

    // remove the event listener
    return () => {
      document.removeEventListener("keydown", handleChangeMode);
    };
  }, [handleChangeMode]);

  // DO NOT REMOVE THIS!
  // this is required to prevent issues with stucked context
  useEffect(() => {
    return () => {
      rootStore.reset();
    };
  }, []);

  return (
    <React.StrictMode>
      <RootStoreContext.Provider value={rootStore}>
        <ThemeProvider theme={rootStore.theme}>
          <App {...props} />
        </ThemeProvider>
      </RootStoreContext.Provider>
    </React.StrictMode>
  );
};

initTracing(
  {
    tracingEnabled: !!process.env.REACT_APP_SENTRY_ENVIRONMENT,
    sentryDSN:
      "https://8e9656ec94b07a091c2de77d0c67f9cc@o1324044.ingest.us.sentry.io/4508499435520000",
    envName: process.env.REACT_APP_SENTRY_ENVIRONMENT,
    tracingRelease: process.env.REACT_APP_TRACING_RELEASE,
    sentryProfilingEnabled: true,
    sentryReplayEnabled: process.env.REACT_APP_SENTRY_ENVIRONMENT === "main",
    sentryTracesSampleRate:
      process.env.REACT_APP_SENTRY_ENVIRONMENT === "main" ? 0.5 : 1.0,
    sentryReplaysOnErrorSampleRate: 1.0,
    sentryReplaysSessionSampleRate: 0.1,
  },
  "oneschema-import-ui",
);

initI18n();
